import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Copy, ImagePlus, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../api";
import { ct } from "../checkoutI18n";

export const PaymentMethodPicker = ({ method, setMethod, lang = "en" }) => {
    const opts = [
        { id: "card", label: ct(lang, "card_label"), sub: ct(lang, "card_sub") },
        { id: "moncash", label: "MonCash", sub: ct(lang, "mobile_sub") },
        { id: "natcash", label: "NatCash", sub: ct(lang, "mobile_sub") },
    ];
    return (
        <div data-testid="payment-method-picker">
            <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand mb-2">{ct(lang, "select_method")}</div>
            <div className="grid grid-cols-3 gap-2">
                {opts.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        onClick={() => setMethod(o.id)}
                        data-testid={`pay-method-${o.id}`}
                        className={`rounded-2xl border px-3 py-3.5 text-center transition-all duration-300 ${
                            method === o.id ? "border-lakou-gold bg-lakou-gold/10" : "border-lakou-gold/15 bg-lakou-raise/50 hover:border-lakou-gold/40"
                        }`}
                    >
                        <div className={`font-display font-bold text-sm ${method === o.id ? "text-lakou-gold" : "text-lakou-cream"}`}>{o.label}</div>
                        <div className="mt-0.5 text-[9px] font-mono2 uppercase tracking-wider text-lakou-sand leading-tight">{o.sub}</div>
                    </button>
                ))}
            </div>
        </div>
    );
};

export const MobileMoneyPanel = ({ order, onReset, lang = "ht" }) => {
    const [sender, setSender] = useState("");
    const [txid, setTxid] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [proofPath, setProofPath] = useState("");
    const [proofBusy, setProofBusy] = useState(false);
    const fileRef = useRef(null);

    const attachProof = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setProofBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/orders/${order.order_id}/proof-image`, fd);
            setProofPath(data.path);
            toast.success(ct(lang, "attached"));
        } catch (err) {
            toast.error(apiError(err, ct(lang, "err_upload")));
        } finally {
            setProofBusy(false);
        }
    };

    const copyRef = async () => {
        try { await navigator.clipboard.writeText(order.reference); toast.success(ct(lang, "copied")); } catch {}
    };

    const submitProof = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/orders/${order.order_id}/proof`, { sender_phone: sender, transaction_id: txid });
            setDone(true);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-lakou-gold/30 bg-lakou-coal/80 p-7 text-center space-y-4" data-testid="mm-done">
                <BadgeCheck size={44} className="mx-auto text-lakou-gold" />
                <div className="font-display font-extrabold text-xl text-lakou-cream" data-testid="mm-done-title">{ct(lang, "done_title")}</div>
                <p className="text-sm text-lakou-grey leading-relaxed" data-testid="mm-done-body">{ct(lang, "done_body")}</p>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand">{ct(lang, "done_event")}</p>
                <div className="inline-flex items-center gap-2 rounded-full border border-lakou-gold/30 px-4 py-1.5 font-mono2 text-xs tracking-[0.2em] text-lakou-gold">{order.reference}</div>
                <div>
                    <span className="inline-flex items-center rounded-full bg-lakou-gold/15 px-4 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-lakou-gold" data-testid="mm-status-pending">
                        {ct(lang, "pending")}
                    </span>
                </div>
                <div>
                    <button onClick={onReset} data-testid="mm-new-order"
                        className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-lakou-gold hover:text-lakou-goldbright transition-colors">
                        {ct(lang, "new_order")}
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-lakou-gold/25 bg-lakou-coal/80 p-7 space-y-5" data-testid="mm-panel">
            <div className="flex items-center gap-3">
                <Smartphone size={18} className="text-lakou-gold" />
                <span className="font-display font-extrabold text-lg text-lakou-cream">{ct(lang, "send_payment")}</span>
            </div>

            <div className="rounded-2xl border border-lakou-gold/15 bg-lakou-raise/50 p-5" data-testid="mm-instructions">
                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-orangelight font-bold">{ct(lang, "instructions_title")}</div>
                <ol className="mt-3 space-y-2 text-xs text-lakou-grey leading-relaxed list-decimal list-inside">
                    {ct(lang, "instructions").map((s, i) => <li key={i}>{s}</li>)}
                </ol>
                <p className="mt-3 text-[11px] font-bold text-lakou-orangelight leading-relaxed" data-testid="mm-important">{ct(lang, "instructions_important")}</p>
            </div>

            <div className="rounded-2xl bg-lakou-raise/70 border border-lakou-gold/15 p-5 text-center">
                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-orangelight font-bold">{ct(lang, "step_send")}</div>
                <div className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand">{order.method === "moncash" ? "MonCash" : "NatCash"}</div>
                <div className="mt-2 font-display font-black text-3xl text-goldgrad" data-testid="mm-amount">{order.amount_htg.toLocaleString("en-US")} HTG</div>
                <div className="mt-1 text-xs text-lakou-grey">${order.amount_usd.toFixed(2)} USD</div>
                <div className="mt-3 font-mono2 text-sm text-lakou-cream" data-testid="mm-number">
                    {ct(lang, "send_to")}: {order.merchant_number}
                </div>
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-dashed border-lakou-gold/40 px-5 py-4">
                <div>
                    <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-lakou-sand">{ct(lang, "tx_ref")}</div>
                    <div className="font-mono2 font-bold text-xl text-lakou-gold tracking-[0.15em]" data-testid="mm-reference">{order.reference}</div>
                </div>
                <button onClick={copyRef} data-testid="mm-copy-ref" aria-label={ct(lang, "copy")} className="p-2.5 rounded-full border border-lakou-gold/25 text-lakou-grey hover:text-lakou-gold transition-colors">
                    <Copy size={15} />
                </button>
            </div>
            <p className="text-xs text-lakou-sand leading-relaxed">{ct(lang, "mm_note")}</p>
            <form onSubmit={submitProof} className="space-y-3" data-testid="mm-proof-form">
                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-orangelight font-bold pt-1">{ct(lang, "step_confirm")}</div>
                <input required value={sender} onChange={(e) => setSender(e.target.value)} placeholder={ct(lang, "sender_ph")} data-testid="mm-sender"
                    className="w-full rounded-xl border border-lakou-gold/25 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold transition-colors" />
                <input value={txid} onChange={(e) => setTxid(e.target.value)} placeholder={ct(lang, "txid_ph")} data-testid="mm-txid"
                    className="w-full rounded-xl border border-lakou-gold/25 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold transition-colors" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={proofBusy} data-testid="mm-attach"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-3 text-xs font-display font-bold uppercase tracking-[0.12em] transition-colors ${proofPath ? "border-lakou-gold text-lakou-gold" : "border-lakou-gold/30 text-lakou-grey hover:text-lakou-cream hover:border-lakou-gold/60"}`}>
                    <ImagePlus size={14} /> {proofBusy ? "…" : proofPath ? ct(lang, "attached") : ct(lang, "attach")}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={attachProof} data-testid="mm-attach-input" />
                <button type="submit" disabled={busy} data-testid="mm-paid"
                    className="w-full rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-4 transition-colors duration-300 disabled:opacity-50">
                    {busy ? "…" : ct(lang, "submit_payment")}
                </button>
            </form>
        </motion.div>
    );
};
